import { ReactElement } from 'react';
import { useRouter } from 'next/router';
import { Link } from '@digital-pulse/react';

import {
  ADDRESS_ROUTE,
  IDENTITY_INFO_ROUTE,
  PERSONAL_INFO_ROUTE,
  PRESCRIPTION_LABEL_ROUTE
} from '../../lib/constants/routes';
import styles from './connectAnotherWayCard.module.scss';
import { clickAnalytics, tags } from '../../lib/utils/analytics';
import { useConnectAnotherWayTranslations } from '../../lib/utils/translationHelpers';
import { isCaregivingAccessFlow } from '@/lib/utils/utils';

interface Props {
  linkProps: {
    label: string;
    href: string;
  }[];
  flow?: string | string[];
}

const ConnectAnotherWayCard: React.FC<Props> = ({ linkProps, flow }): ReactElement => {
  const router = useRouter();
  const { push } = router;
  // Normalize flow which may come from router.query as string | string[]
  const flowValue = Array.isArray(flow) ? flow[0] : flow;
  const flowName = isCaregivingAccessFlow(flowValue) ? tags.cg : tags.self;
  const translations = useConnectAnotherWayTranslations();

  // Use translated heading (fallback handled by translation system)
  const connectAnotherWayHeading = translations?.CONNECT_ANOTHER_WAY;

  const handleTag = (label: string, href: string) => {
    const data = {
      payload: {
        link_name: label.includes('info')
          ? `custom:account:${flowName}:rx_label_page:connect_using_personal_info`
          : `custom:account:${flowName}:lookup_page:connect_using_prescription_label`
      }
    };
    clickAnalytics(data);
    push({
      pathname: href,
      query: router.query
    });
  };

  return (
    <div className={styles.connect_another_way}>
      <hr />
      <h2>{connectAnotherWayHeading}</h2>
      {linkProps &&
        linkProps.map((link: any, index: number) => {
          const { label, href } = link;
          return (
            <div slot="navigation" key={`${index}-link`}>
              <Link
                href="javascript:;"
                arrow-icon="right"
                variant="standalone"
                id={`${index}-link`}
                subtype="high-contrast"
                onClick={() => {
                  handleTag(label, href);
                }}
              >
                {label}
              </Link>
            </div>
          );
        })}
    </div>
  );
};

export default ConnectAnotherWayCard;

// Helper functions to create links with translations
export const getPrescriptionLabelLink = (translations: any) => ({
  label: translations?.CONNECT_ANOTHER_WAY_PRESCRIPTION_LABEL,
  href: PRESCRIPTION_LABEL_ROUTE
});

export const getDemographicLink = (translations: any) => ({
  label: translations?.CONNECT_ANOTHER_WAY_ADDRESS,
  href: ADDRESS_ROUTE
});

export const getPersonalInfoLink = (translations: any) => ({
  label: translations?.CONNECT_ANOTHER_WAY_PERSONAL_INFO,
  href: PERSONAL_INFO_ROUTE
});

export const getIdentityInfoLink = (translations: any) => ({
  label: translations?.CONNECT_ANOTHER_WAY_PERSONAL_INFO,
  href: IDENTITY_INFO_ROUTE
});
